import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardHeader, Badge } from 'reactstrap';
import { Calendar, DollarSign, FileText, Briefcase, Menu } from 'react-feather';
import axios from 'axios';
import PropTypes from 'prop-types';

const ArbitrationExpenseList = ({
    expenseIds = []
}) => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);

    const expenseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense';

    useEffect(() => {
        if (expenseIds && expenseIds.length > 0) {
            fetchExpenseDetails();
        } else {
            setLoading(false);
            setExpenses([]);
        }
    }, [expenseIds]);

    const fetchExpenseDetails = async () => {
        setLoading(true);
        try {
            const expensePromises = expenseIds.map(async (expenseId) => {
                try {
                    const response = await axios.get(`${expenseApiUrl}/${expenseId}`);
                    if (response.data && response.data.isSuccess && response.data.data) {
                        return response.data.data;
                    }
                    return null;
                } catch (error) {
                    console.error(`Error fetching expense ${expenseId}:`, error);
                    return null;
                }
            });

            const expenseResults = await Promise.all(expensePromises);
            const validExpenses = expenseResults.filter(expense => expense !== null);

            console.log('Fetched arbitration expenses:', validExpenses);
            setExpenses(validExpenses);
        } catch (error) {
            console.error('Error fetching expense details:', error);
            setExpenses([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Ödendi':
                return 'success';
            case 'Ödenmedi':
                return 'warning';
            default:
                return 'primary';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return '-';
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <p className="mt-2 text-muted">Masraflar yükleniyor...</p>
            </div>
        );
    }

    if (expenses.length === 0) {
        return (
            <div className="text-center p-4">
                <FileText size={48} className="text-muted mb-3" />
                <p className="text-muted">Bu tahkim için henüz masraf kaydı bulunmamaktadır.</p>
            </div>
        );
    }

    return (
        <div className="expense-card-list">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">
                    Tahkim Başvuru Ücreti
                </h4>
            </div>

            <Row>
                {expenses.map((expense, index) => (
                    <Col key={`expense-${index}`} md={6} lg={4} className="mb-3 d-flex">
                        <Card
                            className="shadow-sm w-100 h-100"
                            style={{ fontSize: '0.95rem' }}
                        >
                            <CardHeader
                                className="d-flex justify-content-between align-items-center text-white py-2"
                                style={{ backgroundColor: '#59667a' }}
                            >
                                <h6 className="mb-2 mt-2" style={{ wordBreak: 'break-word', maxWidth: '70%' }}>
                                    {expense.expenseType || 'Masraf'}
                                </h6>
                                <div>
                                    <Badge
                                        color={getStatusColor(expense.paymentStatus)}
                                        className="px-2 py-1"
                                        style={{ fontSize: '13px', fontWeight: 'bold', borderRadius: '8px' }}
                                    >
                                        {expense.paymentStatus || 'Ödenmedi'}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardBody className="p-3">
                                <div className="mb-2 d-flex align-items-center">
                                    <FileText size={18} className="me-2 text-info" />
                                    <div>
                                        <strong>Masraf No:</strong> {expense.expenseNumber || '-'}
                                    </div>
                                </div>

                                <div className="mb-2 d-flex align-items-center">
                                    <DollarSign size={18} className="me-2 text-success" />
                                    <div>
                                        <strong>Tutar:</strong> {expense.amount ? `${new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(expense.amount)}` : '-'}
                                    </div>
                                </div>

                                <div className="mb-2 d-flex align-items-center">
                                    <Briefcase size={18} className="me-2 text-secondary" />
                                    <div>
                                        <strong>Banka:</strong> {expense.associatedBank && expense.associatedBank.bankName
                                            ? `${expense.associatedBank.bankName}${expense.associatedBank.branchName ? ` - ${expense.associatedBank.branchName}` : ''}`
                                            : '-'}
                                    </div>
                                </div>

                                <div className="mb-2 d-flex align-items-center">
                                    <Calendar size={18} className="me-2 text-warning" />
                                    <div>
                                        <strong>Tarih:</strong> {formatDate(expense.date)}
                                    </div>
                                </div>

                                {expense.invoiceNumber && expense.invoiceNumber !== 0 && (
                                    <div className="mb-2 d-flex align-items-center">
                                        <FileText size={18} className="me-2 text-secondary" />
                                        <div>
                                            <strong>Fatura No:</strong> {expense.invoiceNumber}
                                        </div>
                                    </div>
                                )}

                                {expense.description && (
                                    <div className="d-flex align-items-start mt-2">
                                        <Menu size={18} className="me-2 text-muted mt-1" />
                                        <div style={{ wordBreak: 'break-word', fontSize: '0.85rem' }} title={expense.description}>
                                            <strong>Açıklama:</strong> {expense.description.length > 50 ? `${expense.description.substring(0, 50)}...` : expense.description}
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

ArbitrationExpenseList.propTypes = {
    expenseIds: PropTypes.array
};

export default ArbitrationExpenseList;
